// frontend/src/components/Register.js
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { generateAvatar } from '../api';
import Turnstile from 'react-turnstile';
import AvatarGeneratorModal from './AvatarGeneratorModal';
import styles from './Register.module.css';
import quironLogo from '../assets/quiron-isotipo.png';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://quiron.app/api';

import { useTranslation } from 'react-i18next';

function Register() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  useEffect(() => {
    document.documentElement.setAttribute('data-force-theme', 'dark');
    return () => {
      document.documentElement.removeAttribute('data-force-theme');
    };
  }, []);

  const [step, setStep] = useState(1);
  const [occupation, setOccupation] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [username, setUsername] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [prompt, setPrompt] = useState('');
  const [tempAvatar, setTempAvatar] = useState(null);
  const [captchaToken, setCaptchaToken] = useState(null);
  const [avatarGenerated, setAvatarGenerated] = useState(false);
  const [referralSourceOption, setReferralSourceOption] = useState('');
  const [referralSourceOther, setReferralSourceOther] = useState('');
  const fileInputRef = useRef(null);

  const handleOccupationChange = (e) => setOccupation(e.target.value);

  const handleReferralSourceChange = (e) => {
    setReferralSourceOption(e.target.value);
    if (e.target.value !== t('other')) {
      setReferralSourceOther('');
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      setPreview(URL.createObjectURL(file));
    }
  };

  const handleGenerateAvatar = async () => {
    if (avatarGenerated) {
      alert(t('alreadyGeneratedAvatar'));
      return;
    }
    if (!prompt) {
      alert(t('enterPromptForAvatar'));
      return;
    }
    try {
      const avatarData = await generateAvatar(prompt);
      const avatarUrl = `https://quiron.app/static/uploads/profile_pictures/${avatarData.profile_picture}`;
      setTempAvatar(avatarUrl);
      setAvatarGenerated(true);
    } catch (error) {
      console.error('Erro ao gerar avatar:', error);
      alert(t('errorGeneratingAvatar'));
    }
  };

  const handleSaveAvatar = () => {
    if (!tempAvatar) {
      alert(t('noAvatarToSave'));
      return;
    }
    setSelectedFile(tempAvatar);
    setPreview(tempAvatar);
    setTempAvatar(null);
  };

  const handleDiscardAvatar = () => {
    setTempAvatar(null);
  };

  const handleStep1 = async (e) => {
    e.preventDefault();
    if (!termsAccepted) {
      alert(t('acceptTermsPrivacy'));
      return;
    }
    if (!captchaToken) {
      alert(t('completeCaptcha'));
      return;
    }

    const formData = new FormData();
    formData.append('email', e.target.email.value);
    formData.append('password', e.target.password.value);
    formData.append('full_name', e.target.full_name.value);
    formData.append('occupation', occupation);
    formData.append('phone_number', e.target.phone_number.value);
    const referralValue = referralSourceOption === t('other') ? referralSourceOther : referralSourceOption;
    formData.append('referral_source', referralValue || '');
    formData.append('captcha_token', captchaToken);
    if (occupation === t('medicalStudent')) {
      formData.append('university', e.target.university.value);
      formData.append('period', e.target.period.value);
      formData.append('matricula', e.target.matricula.value);
    } else if (occupation === t('doctor')) {
      formData.append('identifier_type', e.target.identifier_type.value);
      formData.append('identifier_number', e.target.identifier_number.value);
    }
    if (selectedFile) {
      formData.append('file', selectedFile instanceof File ? selectedFile : new Blob([selectedFile], { type: 'image/png' }));
    }

    try {
      const response = await fetch(`${API_BASE_URL}/register/step1`, {
        method: 'POST',
        body: formData,
        credentials: 'include',
      });
      const result = await response.json();
      if (response.ok) {
        setUsername(result.email);
        setStep(2);
        alert(t('checkEmailToContinue'));
      } else {
        alert(result.error);
      }
    } catch (error) {
      alert(t('errorSendingData'));
    }
  };

  const handleStep3 = async (e) => {
    e.preventDefault();
    const token = e.target.token.value;
    try {
      const response = await fetch(`${API_BASE_URL}/register/step2`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: username, token }),
        credentials: 'include',
      });
      const result = await response.json();
      if (response.ok) {
        alert(t('registrationCompleteLogin'));
        navigate('/login');
      } else {
        alert(result.error);
      }
    } catch (error) {
      alert(t('errorValidatingToken'));
    }
  };

  return (
    <div className={styles.authPageWrapper}>
      <div className={styles.logoContainer} onClick={() => navigate('/')}>
        <img src={quironLogo} alt="Quiron Logo" className={styles.logoImg} />
        <h1 className={styles.logoText}>Quiron</h1>
      </div>

      <div className={styles.formContainer}>
        <h2 className={styles.formTitle}>{t('registration')}</h2>

        {step === 1 && (
          <form onSubmit={handleStep1}>
            <div>
              <label className={styles.themeLabel}>{t('email')}</label>
              <input type="email" name="email" placeholder={t('emailPlaceholder')} required className={styles.themeInput} />
            </div>
            <div>
              <label className={styles.themeLabel}>{t('password')}</label>
              <input type="password" name="password" placeholder={t('passwordPlaceholder')} required className={styles.themeInput} />
            </div>
            <div>
              <label className={styles.themeLabel}>{t('fullName')}</label>
              <input type="text" name="full_name" placeholder={t('fullNamePlaceholder')} required className={styles.themeInput} />
            </div>
            <div>
              <label className={styles.themeLabel}>{t('occupation')}</label>
              <select
                name="occupation"
                value={occupation}
                onChange={handleOccupationChange}
                required
                className={styles.themeSelect}
              >
                <option value="">{t('selectOccupation')}</option>
                <option value={t('medicalStudent')}>{t('medicalStudent')}</option>
                <option value={t('doctor')}>{t('doctor')}</option>
              </select>
            </div>
            {occupation === t('medicalStudent') && (
              <>
                <div>
                  <label className={styles.themeLabel}>{t('university')}</label>
                  <input type="text" name="university" placeholder={t('universityPlaceholder')} required className={styles.themeInput} />
                </div>
                <div>
                  <label className={styles.themeLabel}>{t('period')}</label>
                  <input type="text" name="period" placeholder={t('periodPlaceholder')} required className={styles.themeInput} />
                </div>
                <div>
                  <label className={styles.themeLabel}>{t('enrollment')}</label>
                  <input type="text" name="matricula" placeholder={t('enrollmentPlaceholder')} required className={styles.themeInput} />
                </div>
              </>
            )}
            {occupation === t('doctor') && (
              <>
                <div>
                  <label className={styles.themeLabel}>{t('identifier')}</label>
                  <select name="identifier_type" required className={styles.themeSelect}>
                    <option value="">{t('select')}</option>
                    <option value="CRM">CRM</option>
                    <option value="RQE">RQE</option>
                  </select>
                </div>
                <div>
                  <label className={styles.themeLabel}>{t('identifierDigits')}</label>
                  <input type="text" name="identifier_number" placeholder={t('identifierDigitsPlaceholder')} required className={styles.themeInput} />
                </div>
              </>
            )}
            <div>
              <label className={styles.themeLabel}>{t('phone')}</label>
              <input type="text" name="phone_number" placeholder={t('phonePlaceholder')} required className={styles.themeInput} />
            </div>
            <div>
              <label className={styles.themeLabel}>{t('howDidYouHearAboutUs')}</label>
              <select
                name="referral_source_option"
                value={referralSourceOption}
                onChange={handleReferralSourceChange}
                className={styles.themeSelect}
              >
                <option value="">{t('selectAnOption')}</option>
                <option value="YouTube">YouTube</option>
                <option value="Instagram">Instagram</option>
                <option value="TikTok">TikTok</option>
                <option value="X">X ({t('oldTwitter')})</option>
                <option value="LinkedIn">LinkedIn</option>
                <option value={t('friendColleagueReferral')}>{t('friendColleagueReferral')}</option>
                <option value={t('googleSearch')}>{t('googleSearch')}</option>
                <option value={t('eventConference')}>{t('eventConference')}</option>
                <option value={t('onlineAd')}>{t('onlineAd')}</option>
                <option value={t('languageModelReferral')}>{t('languageModelReferral')}</option>
                <option value={t('other')}>{t('other')}</option>
              </select>
            </div>
            {referralSourceOption === t('other') && (
              <div>
                <label className={styles.themeLabel}>{t('specify')}:</label>
                <input
                  type="text"
                  name="referral_source_other"
                  value={referralSourceOther}
                  onChange={(e) => setReferralSourceOther(e.target.value)}
                  placeholder={t('pleaseSpecify')}
                  className={styles.themeInput}
                  required
                />
              </div>
            )}
            <div className={styles.termsGroup}>
              <input
                type="checkbox"
                id="terms"
                checked={termsAccepted}
                onChange={(e) => setTermsAccepted(e.target.checked)}
                required
              />
              <label htmlFor="terms" className={styles.termsLabel}>
                {t('iAcceptThe')} <a href="/terms">{t('termsOfUse')}</a> {t('andThe')}{' '}
                <a href="/privacy">{t('privacyPolicy')}</a>
              </label>
            </div>
            {termsAccepted && (
              <div>
                <Turnstile
                  sitekey={process.env.REACT_APP_CLOUDFLARE_SITE_KEY}
                  onVerify={(token) => setCaptchaToken(token)}
                />
              </div>
            )}
            <div style={{ textAlign: 'center', marginTop: '20px' }}>
              <button type="submit" className={styles.themeButton}>{t('next')}</button>
            </div>
          </form>
        )}

        {step === 2 && (
          <div>
            <h3>{t('generateAvatarOptional')}</h3>
            <AvatarGeneratorModal
              isOpen={true}
              onClose={() => setStep(3)}
              prompt={prompt}
              setPrompt={setPrompt}
              handleGenerateAvatar={handleGenerateAvatar}
              tempAvatar={tempAvatar}
              setTempAvatar={setTempAvatar}
              handleSaveAvatar={handleSaveAvatar}
              handleDiscardAvatar={handleDiscardAvatar}
              avatarHistory={[]}
              handleRemoveAvatar={() => {}}
              addNotification={alert}
            />
            <button onClick={() => setStep(3)} className={styles.themeButton}>{t('skip')}</button>
          </div>
        )}

        {step === 3 && (
          <form onSubmit={handleStep3}>
            <div>
              <label className={styles.themeLabel}>{t('inviteToken')}</label>
              <input type="text" name="token" placeholder={t('inviteTokenPlaceholder')} required className={styles.themeInput} />
            </div>
            <button type="submit" className={styles.themeButton}>{t('finish')}</button>
          </form>
        )}
      </div>
    </div>
  );
}

export default Register;